import { scry } from "@/lib/scryClient";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  getDoc,           // ADD
  doc,
  setDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  documentId,
  writeBatch,
  deleteDoc,
  limitToLast,
  type Timestamp,
  increment,
  deleteField,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { auth, db, storage } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { getCachedProfile, prefetchProfiles } from "@/lib/profileCache";
import {
  acceptOfferV2,
  ensureOfferCardSnapshot,
  subscribeOfferMessages,
  sendOfferMessage,
  type OfferMessage,
} from "@/data/offersV2";

/* ================= Config ================= */

const BOT_AVATAR_SRC = "/images/bob_bot.png";
const SCOREBOARD_BOT_UID = "scoreboard-bot" as const;
const SCOREBOARD_ADMIN_UIDS: string[] = String(
  (import.meta as any)?.env?.VITE_SCOREBOARD_ADMIN_UIDS ??
    (import.meta as any)?.env?.VITE_SCOREBOARD_ADMIN_UID ??
    "",
)
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);
const isScoreboardAdmin = (u?: string | null) =>
  !!u && SCOREBOARD_ADMIN_UIDS.includes(u);

const LEFT_WIDTH_KEY = "inbox:leftWidth";
const MIN_LEFT = 180;
const MAX_LEFT = 540;
const MOBILE_COLLAPSED_WIDTH = 54;

/* ================= Types ================= */

type MinimalUser = {
  uid: string;
  username: string | null;
  avatarUrl: string | null;
  displayName: string | null;
};

type ThreadDoc = {
  id: string;
  participants: string[];
  lastMessage?: string | null;
  lastMessageAt?: Timestamp | null;
  lastSenderId?: string | null;
  unreadCounts?: Record<string, number>;
  deletedFor?: Record<string, any>;
  archivedFor?: Record<string, any>;
};

type MirrorDoc = {
  tid: string;
  otherUid: string;
  lastMessage?: string | null;
  lastMessageAt?: Timestamp | null;
  unread?: number;
};

type MessageDoc = {
  id: string;
  threadId: string;
  senderId: string;
  recipientId: string;
  createdAt?: Timestamp;
  body?: any;
  isRead?: boolean;
  participants?: string[];
  label?: string;
  clientId?: string;
};

type MsgAttachment = {
  name: string;
  url: string;
  contentType: string;
  size: number;
  type: "image" | "text";
};

type OfferDoc = {
  id: string;
  fromUid: string;
  toUid: string;
  offerType: "trade" | "purchase";
  offeredPrice?: number | null;
  offeredItems?: Array<{ name: string; scryfall_id?: string; cardId?: string }>;
  status:
    | "open"
    | "accepted"
    | "declined"
    | "withdrawn"
    | "completed"
    | "countered";
  message?: string | null;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
  lastActorUid?: string | null;
  cardName?: string | null;
  cardImage?: string | null;
  scryfall_id?: string | null;
  cardRefPath?: string | null;
  cardId?: string | null;
  cardOwnerUid?: string | null;
};

/* ================= Helpers ================= */

const tsMillis = (t?: Timestamp | null) => (t?.toMillis?.() ?? 0);
const tsOrNull = (t?: Timestamp | null): number | null =>
  (t && t.toMillis && t.toMillis() > 0 ? t.toMillis() : null);
const formatTs = (t?: Timestamp | null): string =>
  (tsOrNull(t) ? new Date(tsMillis(t)).toLocaleString() : "…");

function dm2ThreadId(a: string, b: string): string {
  const pair = a < b ? `${a}_${b}` : `${b}_${a}`;
  return `dm2:${pair}`;
}
async function resolveThreadId(me: string, peer: string): Promise<string> {
  const tid = dm2ThreadId(me, peer);
  const participants = [me, peer].sort((a, b) => (a < b ? -1 : 1));
  await setDoc(
    doc(db, "threads_v2", tid),
    {
      participants,
      lastMessageAt: serverTimestamp(),
      lastSenderId: me,
      unreadCounts: { [me]: 0, [peer]: 0 },
      messageCount: increment(0),
    },
    { merge: true },
  ).catch(() => {});
  return tid;
}
function msgText(m: MessageDoc): string {
  const b: any = m.body;
  if (!b) return "";
  if (typeof b === "string") return b;
  return b.text ?? b.message ?? "";
}

// --- START PATCH: SCRYFALL BACKEND HELPERS (INBOX) ---
/** Defensive JSON unwrap */
async function sfToJson(res: Response | null | undefined) {
  try {
    if (!res || !("ok" in res) || !res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/** Prefer normal/large; supports multi-face; skip when image_status === "missing" */
function sfPickImage(sf: any): string | null {
  if (!sf || sf.image_status === "missing") return null;
  const direct = sf?.image_uris?.normal ?? sf?.image_uris?.large ?? sf?.image_uris?.small ?? null;
  if (direct) return direct;
  if (Array.isArray(sf?.card_faces)) {
    for (const face of sf.card_faces) {
      const img =
        face?.image_uris?.normal ??
        face?.image_uris?.large ??
        face?.image_uris?.small ??
        null;
      if (img) return img;
    }
  }
  return null;
}

/** Tiny per-run cache to avoid duplicate id fetches */
const __sfByIdCache = new Map<string, Promise<any | null>>();

/** Backend-first fetch by *Scryfall card id* (NOT oracle), fallback to public API */
async function sfFetchByCardIdBackendFirst(cardId: string): Promise<any | null> {
  if (!cardId) return null;
  if (__sfByIdCache.has(cardId)) return __sfByIdCache.get(cardId)!;

  const p = (async () => {
    // Try your server client first (feature-detect likely methods)
    try {
      if (typeof (scry as any)?.getById === "function") {
        const res = await (scry as any).getById(cardId);
        const json = await sfToJson(res);
        if (json?.id) return json;
      }
      if (typeof (scry as any)?.getCard === "function") {
        const res = await (scry as any).getCard({ id: cardId });
        const json = await sfToJson(res);
        if (json?.id) return json;
      }
    } catch {
      // ignore & fallback
    }

    // Public fallback: /cards/{id}
    try {
      const r = await fetch(`https://api.scryfall.com/cards/${encodeURIComponent(cardId)}`);
      return await sfToJson(r);
    } catch {
      return null;
    }
  })();

  __sfByIdCache.set(cardId, p);
  return p;
}
// --- END PATCH: SCRYFALL BACKEND HELPERS (INBOX) ---


/* Scryfall cache */
const scryfallMetaCache = new Map<
  string,
  { name: string | null; image: string | null; ts: number }
>();
// --- START PATCH: REWRITE fetchScryfallCard (INBOX) ---
async function fetchScryfallCard(
  scryId: string,
): Promise<{ name: string | null; image: string | null } | null> {
  if (!scryId) return null;

  // cache hit
  const cached = scryfallMetaCache.get(scryId);
  if (cached) return { name: cached.name, image: cached.image };

  try {
    // Backend-first, fallback public
    const data = await sfFetchByCardIdBackendFirst(scryId);
    if (!data) throw new Error("scryfall not found");

    const name =
      data?.printed_name ??
      data?.faceName ??
      data?.face_name ??
      data?.name ??
      (Array.isArray(data?.card_faces) ? data.card_faces[0]?.name ?? null : null) ??
      null;

    const image = sfPickImage(data);

    scryfallMetaCache.set(scryId, { name, image, ts: Date.now() });
    return { name, image };
  } catch {
    scryfallMetaCache.set(scryId, { name: null, image: null, ts: Date.now() });
    return null;
  }
}
// --- END PATCH: REWRITE fetchScryfallCard (INBOX) ---


function normalizeOfferDoc(id: string, v: any): OfferDoc {
  const createdAt: Timestamp | null =
    (v.createdAt && typeof v.createdAt.toMillis === "function"
      ? v.createdAt
      : v.created_at && typeof v.created_at.toMillis === "function"
      ? v.created_at
      : null) || null;
  const updatedAt: Timestamp | null =
    (v.updatedAt && typeof v.updatedAt.toMillis === "function"
      ? v.updatedAt
      : v.updated_at && typeof v.updated_at.toMillis === "function"
      ? v.updated_at
      : null) || null;
  const offerType: "trade" | "purchase" =
    v.offerType === "purchase" || v.type === "purchase" || v.kind === "purchase"
      ? "purchase"
      : "trade";
  const priceCandidate =
    v.offeredPrice ??
    v.price ??
    v.amount ??
    v.cashOffer ??
    v.cash ??
    (typeof v.priceCents === "number" ? v.priceCents / 100 : null);
  const cardName =
    v.cardName ??
    v.card_name ??
    v.card?.name ??
    v.meta?.cardName ??
    v.meta?.card_name ??
    v.card?.printed_name ??
    v.card?.faceName ??
    v.card?.face_name ??
    null;
  const scry =
    v.scryfall_id ??
    v.card?.scryfall_id ??
    v.card?.scryfallId ??
    v.meta?.scryfall_id ??
    null;
  const rawRefPath =
    v.cardRefPath ??
    v.card_ref_path ??
    v.card?.refPath ??
    v.card?.ref_path ??
    v.meta?.cardRefPath ??
    null;
  const cardId = v.cardId ?? v.card_id ?? v.card?.id ?? null;
  const cardOwnerUid =
    v.cardOwnerUid ??
    v.cardOwner ??
    v.ownerUid ??
    v.card_owner_uid ??
    v.card?.ownerUid ??
    null;
  const derivedRef =
    !rawRefPath && cardOwnerUid && cardId
      ? `users/${cardOwnerUid}/cards/${cardId}`
      : rawRefPath;

  return {
    id,
    fromUid: v.fromUid,
    toUid: v.toUid,
    offerType,
    offeredPrice:
      typeof priceCandidate === "number" && Number.isFinite(priceCandidate)
        ? priceCandidate
        : null,
    offeredItems: v.offeredItems ?? v.items ?? null,
    status: v.status,
    message: v.message ?? v.initialMessage ?? v.preview ?? v.meta?.message ?? null,
    createdAt,
    updatedAt,
    lastActorUid: v.lastActorUid ?? null,
    cardName,
    cardImage:
      v.cardImage ??
      v.card?.image ??
      v.card?.image_normal ??
      v.meta?.cardImage ??
      null,
    scryfall_id: scry,
    cardRefPath: derivedRef ? derivedRef.replace(/^\/+/, "") : null,
    cardId,
    cardOwnerUid,
  };
}

/* ================= Image Attachment Spec Helpers (NEW) ================= */

// Spec:
// Accepted input formats: PNG, JPEG, GIF (others: TIFF/BMP/WEBP auto-converted to JPEG).
// Max file size (after processing): 8 MB
// Max width: 1080px (images wider are downscaled, aspect preserved)
// Supported aspect ratios: 1:1, 4:5 (0.8), 1.91:1 (~1.91). Others are center–cropped to nearest.
// Animated GIFs: we do not transform (to preserve animation). They must already satisfy width<=1080
//   and aspect within tolerated range; otherwise they are rejected.

const IMAGE_MAX_BYTES = 8 * 1024 * 1024;
const IMAGE_MAX_WIDTH = 1080;
const ALLOWED_RATIOS = [1, 4 / 5, 1.91];
const RATIO_TOLERANCE = 0.03; // ±3%

// Extensions we let user pick; we'll still process/convert as needed.
const RAW_ACCEPT_MIME = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "image/tiff",
  "image/bmp",
];

function mimeToExt(m: string) {
  if (m.includes("png")) return "png";
  if (m.includes("jpeg") || m.includes("jpg")) return "jpg";
  if (m.includes("gif")) return "gif";
  return "jpg";
}

function isAnimatedGif(_file: File): boolean {
  // Lightweight heuristic skipped (would require deeper parsing).
  // We assume any GIF might be animated; to preserve animation we don't process GIF frames.
  return _file.type === "image/gif";
}

function ratioWithinAllowed(r: number): boolean {
  return ALLOWED_RATIOS.some((ar) => Math.abs(r - ar) <= RATIO_TOLERANCE);
}

function nearestAllowedRatio(r: number): number {
  return ALLOWED_RATIOS.reduce((best, ar) =>
    Math.abs(r - ar) < Math.abs(r - best) ? ar : best
  , ALLOWED_RATIOS[0]);
}

async function processStillImage(
  file: File,
): Promise<{ processed: File; warnings: string[] }> {
  const warnings: string[] = [];
  const dataURL: string = await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result as string);
    fr.onerror = () => rej(fr.error);
    fr.readAsDataURL(file);
  });
  const img: HTMLImageElement = await new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = (e) => rej(e);
    im.src = dataURL;
  });

  let { width, height } = img;
  const originalRatio = width / height;
  let targetRatio = originalRatio;
  let cropX = 0;
  let cropY = 0;
  let cropW = width;
  let cropH = height;

  if (!ratioWithinAllowed(originalRatio)) {
    targetRatio = nearestAllowedRatio(originalRatio);
    warnings.push(
      `Cropped to supported ratio ${targetRatio.toFixed(2)} (was ${originalRatio.toFixed(2)})`,
    );
    // Center crop to target ratio
    if (originalRatio > targetRatio) {
      // too wide
      cropW = Math.round(height * targetRatio);
      cropX = Math.floor((width - cropW) / 2);
    } else {
      // too tall
      cropH = Math.round(width / targetRatio);
      cropY = Math.floor((height - cropH) / 2);
    }
  }

  // Downscale if wider than max width (after crop)
  const croppedWidth = cropW;
  const croppedHeight = cropH;
  let renderW = croppedWidth;
  let renderH = croppedHeight;
  if (renderW > IMAGE_MAX_WIDTH) {
    const scale = IMAGE_MAX_WIDTH / renderW;
    renderW = Math.round(renderW * scale);
    renderH = Math.round(renderH * scale);
    warnings.push(`Downscaled to width ${renderW}px (max ${IMAGE_MAX_WIDTH})`);
  }

  const canvas = document.createElement("canvas");
  canvas.width = renderW;
  canvas.height = renderH;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(
    img,
    cropX,
    cropY,
    cropW,
    cropH,
    0,
    0,
    renderW,
    renderH,
  );

  // Decide output format: keep PNG if original was PNG and has transparency; else JPEG.
  // Simple transparency check:
  let wantPng = file.type === "image/png";
  if (wantPng) {
    const sample = ctx.getImageData(0, 0, Math.min(32, renderW), Math.min(32, renderH)).data;
    let hasAlpha = false;
    for (let i = 3; i < sample.length; i += 4) {
      if (sample[i] < 255) {
        hasAlpha = true;
        break;
      }
    }
    if (!hasAlpha) wantPng = false; // can safely JPEG
  }

  let quality = 0.9;
  let blob: Blob = await new Promise((res) =>
    canvas.toBlob(
      (b) => res(b as Blob),
      wantPng ? "image/png" : "image/jpeg",
      quality,
    ),
  );

  if (blob.size > IMAGE_MAX_BYTES && !wantPng) {
    // Iteratively reduce quality
    while (blob.size > IMAGE_MAX_BYTES && quality > 0.3) {
      quality -= 0.1;
      blob = await new Promise((res) =>
        canvas.toBlob(
          (b) => res(b as Blob),
          "image/jpeg",
          quality,
        ),
      );
    }
    if (blob.size > IMAGE_MAX_BYTES) {
      warnings.push(
        "Could not compress under size limit; final file may be rejected.",
      );
    } else {
      warnings.push(
        `Compressed (quality=${quality.toFixed(2)}) to ${(
          blob.size /
          1024 /
          1024
        ).toFixed(2)}MB`,
      );
    }
  }

  // Enforce final size limit
  if (blob.size > IMAGE_MAX_BYTES) {
    throw new Error(
      `Image still exceeds ${(
        IMAGE_MAX_BYTES /
        1024 /
        1024
      ).toFixed(0)}MB after processing`,
    );
  }

  const ext = wantPng ? "png" : "jpg";
  const newNameBase = file.name.replace(/\.[^.]+$/, "");
  const newFile = new File([blob], `${newNameBase}_proc.${ext}`, {
    type: wantPng ? "image/png" : "image/jpeg",
  });
  return { processed: newFile, warnings };
}

async function ensureImageCompliance(
  file: File,
): Promise<{ ok: boolean; file?: File; warnings?: string[]; reason?: string }> {
  if (!RAW_ACCEPT_MIME.includes(file.type)) {
    return {
      ok: false,
      reason: `Unsupported type ${file.type}`,
    };
  }
  if (file.size > IMAGE_MAX_BYTES) {
    // We will only attempt to process (compress) non-GIF files
    if (file.type === "image/gif") {
      return {
        ok: false,
        reason: "GIF exceeds 8MB limit",
      };
    }
  }

  // GIF (likely animated) – only check width & ratio (no processing)
  if (isAnimatedGif(file)) {
    try {
      const dataURL: string = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result as string);
        fr.onerror = () => rej(fr.error);
        fr.readAsDataURL(file);
      });
      const img: HTMLImageElement = await new Promise((res, rej) => {
        const im = new Image();
        im.onload = () => res(im);
        im.onerror = (e) => rej(e);
        im.src = dataURL;
      });
      const ratio = img.width / img.height;
      if (img.width > IMAGE_MAX_WIDTH) {
        return {
          ok: false,
          reason: `Animated GIF width ${img.width}px exceeds ${IMAGE_MAX_WIDTH}px`,
        };
      }
      if (!ratioWithinAllowed(ratio)) {
        return {
          ok: false,
          reason: `Animated GIF aspect ${ratio.toFixed(
            2,
          )} not in allowed ratios`,
        };
      }
      if (file.size > IMAGE_MAX_BYTES) {
        return {
          ok: false,
          reason: "Animated GIF exceeds 8MB after checks",
        };
      }
      return { ok: true, file, warnings: [] };
    } catch {
      return {
        ok: false,
        reason: "Could not inspect GIF",
      };
    }
  }

  // Still image (PNG/JPEG/WebP/TIFF/BMP) – process pipeline
  try {
    const { processed, warnings } = await processStillImage(file);
    return { ok: true, file: processed, warnings };
  } catch (e: any) {
    return { ok: false, reason: e?.message || "Processing failed" };
  }
}

/* ================= Main Component ================= */

export default function InboxDocked() {
  const navigate = useNavigate();

  /* ---------- Auth / Admin ---------- */
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [ready, setReady] = useState<boolean>(Boolean(auth.currentUser));
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid ?? null);
      setReady(true);
    });
    return () => unsub();
  }, []);

  const [myUsername, setMyUsername] = useState<string | null>(null);
  const amAdmin = useMemo(
    () => isScoreboardAdmin(uid) || myUsername?.toLowerCase?.() === "evictme",
    [uid, myUsername],
  );
  useEffect(() => {
    if (!uid) {
      setMyUsername(null);
      return;
    }
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "users"), where(documentId(), "==", uid)),
        );
        const v: any = snap.docs[0]?.data?.() ?? null;
        setMyUsername(
          v?.username ?? v?.display_name ?? v?.handle ?? v?.displayName ?? null,
        );
      } catch {
        setMyUsername(null);
      }
    })();
  }, [uid]);

  /* ---------- Responsive / Layout state ---------- */
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [leftWidth, setLeftWidth] = useState<number>(() => {
    if (typeof window === "undefined") return 300;
    const stored = Number(localStorage.getItem(LEFT_WIDTH_KEY));
    if (stored && stored >= MIN_LEFT && stored <= MAX_LEFT) return stored;
    return 300;
  });
  const [resizing, setResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const onDragStart = (e: React.MouseEvent) => {
    if (isMobile) return;
    setResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = leftWidth;
    document.body.style.userSelect = "none";
  };
  useEffect(() => {
    if (!resizing) return;
    const onMove = (e: MouseEvent) => {
      const delta = e.clientX - startXRef.current;
      let next = startWidthRef.current + delta;
      if (next < MIN_LEFT) next = MIN_LEFT;
      if (next > MAX_LEFT) next = MAX_LEFT;
      setLeftWidth(next);
    };
    const onUp = () => {
      setResizing(false);
      localStorage.setItem(LEFT_WIDTH_KEY, String(leftWidth));
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [resizing, leftWidth, isMobile]);

  /* ---------- Thread + Mirrors ---------- */
  const [threadsRaw, setThreadsRaw] = useState<ThreadDoc[]>([]);
  const [mirrorsRaw, setMirrorsRaw] = useState<MirrorDoc[]>([]);

  useEffect(() => {
    if (!uid) {
      setThreadsRaw([]);
      return;
    }
    let unsub: (() => void) | null = null;
    let triedFallback = false;
    let stopped = false;

    function wire(queryRef: any, isFallback: boolean) {
      try {
        unsub = onSnapshot(
          queryRef,
          (snap) => {
            if (stopped) return;
            const rows: ThreadDoc[] = snap.docs.map((d) => {
              const v: any = d.data();
              return {
                id: d.id,
                participants: Array.isArray(v.participants) ? v.participants : [],
                lastMessage: v.lastMessage ?? null,
                lastMessageAt: v.lastMessageAt ?? null,
                lastSenderId: v.lastSenderId ?? null,
                unreadCounts: v.unreadCounts ?? {},
                deletedFor: v.deletedFor ?? {},
                archivedFor: v.archivedFor ?? {},
              };
            });
            setThreadsRaw(rows);
          },
          (err) => {
            console.warn("[inbox][threads] listener error", {
              code: err?.code,
              message: err?.message,
              fallbackTried: triedFallback,
            });
            if (err?.code === "permission-denied") {
              if (!isFallback && !triedFallback) {
                triedFallback = true;
                // Fallback: drop orderBy to reduce index / rule surface
                const q2 = query(
                  collection(db, "threads_v2"),
                  where("participants", "array-contains", uid),
                  limit(500),
                );
                wire(q2, true);
              } else {
                // Stop looping; surface minimal notice
                toast({ description: "Some conversations are currently unreadable." });
              }
            }
          },
        );
      } catch (e) {
        console.warn("[inbox][threads] unexpected setup error", e);
      }
    }

    // Primary (ordered) query
    const qPrimary = query(
      collection(db, "threads_v2"),
      where("participants", "array-contains", uid),
      orderBy("lastMessageAt", "desc"),
      limit(500),
    );
    wire(qPrimary, false);

    return () => {
      stopped = true;
      try { unsub?.(); } catch {}
    };
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setMirrorsRaw([]);
      return;
    }
    const qM = query(
      collection(db, "users", uid, "inbox_threads"),
      orderBy("lastMessageAt", "desc"),
      limit(500),
    );
    const unsub = onSnapshot(
      qM,
      (snap) => {
        const rows: MirrorDoc[] = snap.docs.map((d) => {
          const v: any = d.data();
          return {
            tid: v.tid || d.id,
            otherUid: v.otherUid,
            lastMessage: v.lastMessage ?? null,
            lastMessageAt: v.lastMessageAt ?? null,
            unread: v.unread ?? 0,
          };
        });
        setMirrorsRaw(rows);
      },
      () => {},
    );
    return () => unsub();
  }, [uid]);

  type ConversationRow = {
    peerUid: string;
    latestTid: string | null;
    lastMessage: string | null;
    lastMessageAt: Timestamp | null;
    unread: number;
    archived?: boolean;
  };

  const conversations: ConversationRow[] = useMemo(() => {
    if (!uid) return [];
    const byPeer = new Map<string, ConversationRow>();
    for (const t of threadsRaw) {
      if (!Array.isArray(t.participants) || t.participants.length < 2) continue;
      if (t.deletedFor?.[uid]) continue; // hide deleted
      const peer =
        t.participants[0] === uid ? t.participants[1] : t.participants[0];
      const existing = byPeer.get(peer);
      const unread = t.unreadCounts?.[uid] ?? 0;
      const archived = !!t.archivedFor?.[uid];
      const row: ConversationRow = {
        peerUid: peer,
        latestTid: t.id,
        lastMessage: t.lastMessage ?? null,
        lastMessageAt: (t.lastMessageAt as any) ?? null,
        unread,
        archived,
      };
      if (!existing) {
        byPeer.set(peer, row);
      } else {
        const oldMs = tsMillis(existing.lastMessageAt);
        const newMs = tsMillis(row.lastMessageAt);
        if (newMs > oldMs) byPeer.set(peer, row);
        else if (existing.unread < unread)
          byPeer.set(peer, { ...existing, unread });
      }
    }
    for (const m of mirrorsRaw) {
      const peer = m.otherUid;
      const existing = byPeer.get(peer);
      const row: ConversationRow = {
        peerUid: peer,
        latestTid: m.tid,
        lastMessage: m.lastMessage ?? null,
        lastMessageAt: (m.lastMessageAt as any) ?? null,
        unread: m.unread ?? 0,
        archived: false,
      };
      if (!existing) {
        byPeer.set(peer, row);
      } else {
        const oldMs = tsMillis(existing.lastMessageAt);
        const newMs = tsMillis(row.lastMessageAt);
        if (newMs > oldMs) byPeer.set(peer, row);
      }
    }
    const list = Array.from(byPeer.values());
    list.sort(
      (a, b) =>
        tsMillis(b.lastMessageAt ?? null) - tsMillis(a.lastMessageAt ?? null),
    );
    return list;
  }, [uid, threadsRaw, mirrorsRaw]);

  /* ---------- Profiles ---------- */
  const [profiles, setProfiles] = useState<Record<string, MinimalUser>>({});
  useEffect(() => {
    const ids = Array.from(new Set(conversations.map((c) => c.peerUid)));
    if (!ids.length) return;
    void prefetchProfiles(ids).then(() => {
      const out: Record<string, MinimalUser> = {};
      ids.forEach((id) => {
        const p: any = getCachedProfile(id);
        out[id] = {
          uid: id,
          username: p?.username ?? id.slice(0, 12),
          avatarUrl: p?.avatar_url ?? p?.photoURL ?? null,
          displayName: p?.display_name ?? p?.displayName ?? null,
        };
      });
      setProfiles(out);
    });
  }, [conversations]);

  const userMini = useCallback(
    (u?: string | null): { username: string; avatar: string | null } => {
      if (!u) return { username: "user", avatar: null };
      if (u === SCOREBOARD_BOT_UID)
        return { username: "scoreboard", avatar: BOT_AVATAR_SRC };
      const p = profiles[u];
      return {
        username: p?.username ?? "user",
        avatar: p?.avatarUrl ?? null,
      };
    },
    [profiles],
  );

  /* ---------- Selection ---------- */
  const [selectedPeer, setSelectedPeer] = useState<string | null>(null);
  const selectedRow = useMemo(
    () => conversations.find((c) => c.peerUid === selectedPeer) ?? null,
    [conversations, selectedPeer],
  );
  const selectedTid = selectedRow?.latestTid ?? null;

  useEffect(() => {
    if (!selectedPeer && conversations.length) {
      setSelectedPeer(conversations[0].peerUid);
    }
  }, [conversations, selectedPeer]);

  /* ---------- Messages ---------- */
  const [messages, setMessages] = useState<MessageDoc[]>([]);
  const [pending, setPending] = useState<
    Array<
      Omit<MessageDoc, "id" | "createdAt"> & {
        id: string;
        createdAt: { toMillis: () => number };
        _pendingStatus: "sending" | "failed";
      }
    >
  >([]);
  const listRef = useRef<HTMLDivElement | null>(null);
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "auto") => {
      const el = listRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior });
    },
    [],
  );

  useEffect(() => {
    setMessages([]);
    if (!selectedTid) return;
    let unsubTop: (() => void) | null = null;
    let unsubSub: (() => void) | null = null;
    let topCache: MessageDoc[] = [];
    let subCache: MessageDoc[] = [];
    const merge = () => {
      const map = new Map<string, MessageDoc>();
      for (const m of [...topCache, ...subCache]) map.set(m.id, m);
      const arr = Array.from(map.values()).sort((a, b) => {
        const ta = tsMillis(a.createdAt);
        const tb = tsMillis(b.createdAt);
        if (ta !== tb) return ta - tb;
        return a.id.localeCompare(b.id);
      });
      setMessages(arr);
      requestAnimationFrame(() => scrollToBottom("auto"));
    };
    try {
      const qTop = query(
        collection(db, "messages_v2"),
        where("threadId", "==", selectedTid),
        orderBy("createdAt", "asc"),
        limitToLast(500),
      );
      unsubTop = onSnapshot(
        qTop,
        (snap) => {
          topCache = snap.docs.map((d) => {
            const v: any = d.data();
            return {
              id: d.id,
              threadId: v.threadId ?? selectedTid,
              senderId: v.senderId,
              recipientId: v.recipientId,
              createdAt: v.createdAt,
              body: v.body ?? v.text ?? v.content,
              isRead: Boolean(v.isRead),
              participants: v.participants,
              label: v.label,
              clientId: v.clientId,
            };
          });
          merge();
        },
        async () => {
          const qF = query(
            collection(db, "messages_v2"),
            where("threadId", "==", selectedTid),
            limit(1000),
          );
            const snap = await getDocs(qF);
          topCache = snap.docs.map((d) => {
            const v: any = d.data();
            return {
              id: d.id,
              threadId: v.threadId ?? selectedTid,
              senderId: v.senderId,
              recipientId: v.recipientId,
              createdAt: v.createdAt,
              body: v.body ?? v.text ?? v.content,
              isRead: Boolean(v.isRead),
              participants: v.participants,
              label: v.label,
              clientId: v.clientId,
            };
          });
          topCache.sort((a, b) => tsMillis(a.createdAt) - tsMillis(b.createdAt));
          merge();
        },
      );
      const qSub = query(
        collection(db, "threads_v2", selectedTid, "messages_v2"),
        orderBy("createdAt", "asc"),
        limitToLast(500),
      );
      unsubSub = onSnapshot(
        qSub,
        (snap) => {
          subCache = snap.docs.map((d) => {
            const v: any = d.data();
            return {
              id: d.id,
              threadId: selectedTid,
              senderId: v.senderId,
              recipientId: v.recipientId,
              createdAt: v.createdAt,
              body: v.body ?? v.text ?? v.content,
              isRead: Boolean(v.isRead),
              participants: v.participants,
              label: v.label,
              clientId: v.clientId,
            };
          });
          merge();
        },
        () => {},
      );
    } catch (e) {
      console.warn("[inbox] msg subs error", e);
    }
    if (uid && selectedTid) {
      void updateDoc(doc(db, "threads_v2", selectedTid), {
        [`unreadCounts.${uid}`]: 0,
      }).catch(() => {});
    }
    return () => {
      try {
        unsubTop?.();
      } catch {}
      try {
        unsubSub?.();
      } catch {}
    };
  }, [uid, selectedTid, scrollToBottom]);

  useEffect(() => {
    if (!messages.length) return;
    setPending((prev) =>
      prev.filter(
        (p) => !messages.some((m) => m.clientId && m.clientId === p.clientId),
      ),
    );
    requestAnimationFrame(() => scrollToBottom("smooth"));
  }, [messages, scrollToBottom]);

  useEffect(() => {
    scrollToBottom("auto");
  }, [selectedTid, scrollToBottom]);

  /* ---------- Offers ---------- */
  const [offers, setOffers] = useState<OfferDoc[]>([]);
  const [offerThumbs, setOfferThumbs] = useState<
    Record<string, { name?: string | null; image?: string | null }>
  >({});
  const [offerMsgs, setOfferMsgs] = useState<OfferMessage[]>([]);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);

  const [offerTab, setOfferTab] = useState<"received" | "sent" | "completed">(
    "received",
  );
  const [offersCollapsed, setOffersCollapsed] = useState(true);

  useEffect(() => {
    // collapse offers when changing conversation
    setOffersCollapsed(true);
    setSelectedOfferId(null);
    setOfferMsgs([]);
  }, [selectedPeer]);

  useEffect(() => {
    if (selectedOfferId) setOffersCollapsed(true);
  }, [selectedOfferId]);

  useEffect(() => {
    if (!uid || !selectedPeer) {
      setOffers([]);
      return;
    }
    const qRecv = query(
      collection(db, "offers_v2"),
      where("toUid", "==", uid),
      where("fromUid", "==", selectedPeer),
      limit(200),
    );
    const qSent = query(
      collection(db, "offers_v2"),
      where("fromUid", "==", uid),
      where("toUid", "==", selectedPeer),
      limit(200),
    );

    const unsub1 = onSnapshot(qRecv, (snap) => {
      const rows = snap.docs.map((d) => normalizeOfferDoc(d.id, d.data()));
      setOffers((prev) => {
        const others = prev.filter(
          (o) => !(o.toUid === uid && o.fromUid === selectedPeer),
        );
        const next = [...rows, ...others];
        next.sort(
          (a, b) =>
            (tsOrNull(b.updatedAt) ?? tsOrNull(b.createdAt) ?? 0) -
            (tsOrNull(a.updatedAt) ?? tsOrNull(a.createdAt) ?? 0),
        );
        return next;
      });
    });
    const unsub2 = onSnapshot(qSent, (snap) => {
      const rows = snap.docs.map((d) => normalizeOfferDoc(d.id, d.data()));
      setOffers((prev) => {
        const others = prev.filter(
          (o) => !(o.fromUid === uid && o.toUid === selectedPeer),
        );
        const next = [...rows, ...others];
        next.sort(
          (a, b) =>
            (tsOrNull(b.updatedAt) ?? tsOrNull(b.createdAt) ?? 0) -
            (tsOrNull(a.updatedAt) ?? tsOrNull(a.createdAt) ?? 0),
        );
        return next;
      });
    });

    return () => {
      try {
        unsub1();
      } catch {}
      try {
        unsub2();
      } catch {}
    };
  }, [uid, selectedPeer]);

  // Filtered sets
  const receivedOffers = offers.filter(
    (o) => o.toUid === uid && o.status !== "completed",
  );
  const sentOffers = offers.filter(
    (o) => o.fromUid === uid && o.status !== "completed",
  );
  const completedOffers = offers.filter((o) => o.status === "completed");

  // Thumbs / meta
  useEffect(() => {
    const missing = offers.filter((o) => !offerThumbs[o.id]).slice(0, 24);
    if (!missing.length) return;
    const unsubs: Array<() => void> = [];
    for (const o of missing) {
      if (o.cardRefPath) {
        const segs = o.cardRefPath.split("/").filter(Boolean);
        if (segs.length === 4) {
          const r = doc(db, segs[0], segs[1], segs[2], segs[3]);
          const unsub = onSnapshot(
            r,
            (snap) => {
              if (!snap.exists()) return;
              const v: any = snap.data();
              const name =
                v.name ||
                v.printed_name ||
                v.faceName ||
                v.face_name ||
                v.card_name ||
                v.safe_name ||
                null;
              const image =
                v.thumb ||
                v.cardThumb ||
                v.image_thumb ||
                v.image_small ||
                v.image ||
                v.card_image ||
                v.image_normal ||
                v.custom_image_url ||
                v.img ||
                v?.scryfall_full?.image_uris?.small ||
                v?.scryfall_data?.image_uris?.small ||
                null;
              setOfferThumbs((prev) => ({
                ...prev,
                [o.id]: {
                  name: prev[o.id]?.name || name,
                  image: prev[o.id]?.image || image,
                },
              }));
            },
            () => {},
          );
          unsubs.push(unsub);
          continue;
        }
      }
      (async () => {
        const scry =
          o.scryfall_id || o.offeredItems?.[0]?.scryfall_id || null;
        if (!scry) return;
        const meta = await fetchScryfallCard(scry);
        if (!meta) return;
        setOfferThumbs((prev) => ({
          ...prev,
          [o.id]: {
            name: prev[o.id]?.name || meta.name,
            image: prev[o.id]?.image || meta.image,
          },
        }));
      })();
    }
    return () => {
      for (const u of unsubs) {
        try {
          u();
        } catch {}
      }
    };
  }, [offers, offerThumbs]);

  // Offer message thread
  useEffect(() => {
    if (!selectedOfferId) {
      setOfferMsgs([]);
      return;
    }
    const unsub = subscribeOfferMessages(selectedOfferId, (rows) => {
      const normalized = rows
        .map((m: any) => ({
          ...m,
          text: m.text ?? m.body?.text ?? m.body?.message ?? m.message ?? "",
        }))
        .sort((a: any, b: any) => {
          const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          if (ta !== tb) return ta - tb;
          return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
        });
      setOfferMsgs(normalized);
      requestAnimationFrame(() => scrollToBottom("smooth"));
    });
    const active = offers.find((o) => o.id === selectedOfferId);
    if (active && (!active.cardImage || !active.cardName) && active.cardRefPath) {
      ensureOfferCardSnapshot({
        id: active.id,
        cardRefPath: active.cardRefPath,
      }).catch(() => {});
    }
    return () => {
      try {
        unsub?.();
      } catch {}
    };
  }, [selectedOfferId, offers, scrollToBottom]);

  /* ---------- Composer ---------- */
  const [input, setInput] = useState("");
  const [offerReply, setOfferReply] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const offerTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const ATTACH_ACCEPT = RAW_ACCEPT_MIME as const; // (updated)
  // Replace 2MB limit with 8MB spec limit for images
  const ATTACH_MAX_BYTES = IMAGE_MAX_BYTES;
  const ATTACH_MAX_COUNT = 3;

  const focusComposer = useCallback(() => {
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);
  const focusOfferComposer = useCallback(() => {
    requestAnimationFrame(() => offerTextareaRef.current?.focus());
  }, []);

  async function uploadAllAttachments(
    me: string,
    tid: string,
    fs: File[],
  ): Promise<MsgAttachment[]> {
    await debugThread(tid);

    const out: MsgAttachment[] = [];
    const slice = fs.slice(0, ATTACH_MAX_COUNT);
    const now = Date.now();
    const expires = now + 10 * 24 * 60 * 60 * 1000;

    for (const f of slice) {
      if (!ATTACH_ACCEPT.includes(f.type as (typeof ATTACH_ACCEPT)[number])) {
        console.warn("[attach] reject mime", f.type);
        toast({ description: `Unsupported type ${f.type}` });
        continue;
      }
      if (f.size > ATTACH_MAX_BYTES) {
        toast({ description: `${f.name} > 8MB` });
        continue;
      }
      const safeName = f.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `threads/${tid}/attachments/${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}_${safeName}`;
      const refObj = storageRef(storage, path);
      try {
        console.log("[attach] uploading", { path, size: f.size, type: f.type });
        await uploadBytes(refObj, f, {
          contentType: f.type,
          customMetadata: {
            threadId: tid,
            sender: me,
            createdAtMs: String(now),
            expiresAtMs: String(expires),
          },
        });
        const url = await getDownloadURL(refObj);
        console.log("[attach] uploaded ok", { path, url });
        out.push({
          name: f.name,
          url,
          contentType: f.type,
          size: f.size,
          type: f.type.startsWith("image/") ? "image" : "text",
        });
      } catch (err: any) {
        console.error("[attach] upload error", {
          path,
          code: err?.code,
          message: err?.message,
          err,
        });
        toast({ description: "Attachment upload denied" });
      }
    }
    return out;
  }

  function confirmDeliveryFor(docPath: string, clientId: string) {
    try {
      const r = doc(db, docPath);
      const timeout = setTimeout(() => {
        setPending((prev) =>
          prev.map((p) =>
            p.clientId === clientId && p._pendingStatus === "sending"
              ? { ...p, _pendingStatus: "failed" }
              : p,
          ),
        );
      }, 10000);
      const unsub = onSnapshot(
        r,
        { includeMetadataChanges: true },
        (snap) => {
          if (!snap.exists()) return;
          if (!snap.metadata.hasPendingWrites) {
            clearTimeout(timeout);
            setPending((prev) => prev.filter((p) => p.clientId !== clientId));
            unsub();
          }
        },
        () => {
          clearTimeout(timeout);
          setPending((prev) =>
            prev.map((p) =>
              p.clientId === clientId ? { ...p, _pendingStatus: "failed" } : p,
            ),
          );
        },
      );
    } catch {
      setPending((prev) =>
        prev.map((p) =>
          p.clientId === clientId ? { ...p, _pendingStatus: "failed" } : p,
        ),
      );
    }
  }

  async function handleSendDM(): Promise<void> {
    const me = uid;
    const peer = selectedPeer;
    const text = input.trim();
    if (!me || !peer) return;
    if (!text && files.length === 0) return;
    const newsMode = peer === SCOREBOARD_BOT_UID;
    if (newsMode && !amAdmin) {
      toast({ description: "Only admins can post Scoreboard News." });
      return;
    }
    const tid = selectedTid || (await resolveThreadId(me, peer));
    // Ensure thread has participants array for rules & queries
    await ensureThreadParticipants(tid, me, peer);

    let attachments: MsgAttachment[] = [];
    if (files.length > 0) {
      setUploading(true);
      try {
        attachments = await uploadAllAttachments(me, tid, files);
      } finally {
        setUploading(false);
      }
    }
    const clientId = `${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    setPending((prev) => [
      ...prev,
      {
        id: `pending:${clientId}`,
        threadId: tid,
        senderId: me,
        recipientId: peer,
        body: { text, attachments },
        createdAt: { toMillis: () => Date.now() } as any,
        _pendingStatus: "sending",
        label: newsMode ? "news" : "c",
        participants: [me, peer],
        clientId,
        isRead: false,
      },
    ]);
    scrollToBottom("smooth");
    try {
      const participants = [me, peer].sort((a, b) => (a < b ? -1 : 1));
      if (newsMode) {
        const ref = await addDoc(
          collection(db, "threads_v2", tid, "messages_v2"),
          {
            label: "news",
            senderId: me,
            recipientId: peer,
            participants,
            body: { text, attachments },
            createdAt: serverTimestamp(),
            isRead: false,
            clientId,
          },
        );
        confirmDeliveryFor(`threads_v2/${tid}/messages_v2/${ref.id}`, clientId);
      } else {
        const ref = await addDoc(collection(db, "messages_v2"), {
          label: "c",
          threadId: tid,
          senderId: me,
          recipientId: peer,
          participants,
          body: { text, attachments },
          createdAt: serverTimestamp(),
          isRead: false,
          clientId,
        });
        confirmDeliveryFor(`messages_v2/${ref.id}`, clientId);
      }
    } catch (e: any) {
      setPending((prev) =>
        prev.map((p) =>
          p.clientId === clientId ? { ...p, _pendingStatus: "failed" } : p,
        ),
      );
      toast({ description: e?.message ?? "Could not send." });
      return;
    }
    void setDoc(
      doc(db, "threads_v2", tid),
      {
        participants: [me, peer].sort((a, b) => (a < b ? -1 : 1)),
        lastMessage:
          text ||
          (attachments.length
            ? `[${attachments.length} attachment${
                attachments.length > 1 ? "s" : ""
              }]`
            : ""),
        lastMessageAt: serverTimestamp(),
        lastSenderId: me,
        [`unreadCounts.${peer}`]: increment(1),
        messageCount: increment(1),
      },
      { merge: true },
    ).catch(() => {});
    setInput("");
    setFiles([]);
    requestAnimationFrame(() => scrollToBottom("smooth"));
  }

  async function handleSendOfferMsg(): Promise<void> {
    if (!selectedOfferId) return;
    const text = offerReply.trim();
    if (!text) return;
    try {
      await sendOfferMessage(selectedOfferId, text);
      await updateDoc(doc(db, "offers_v2", selectedOfferId), {
        message: text,
        lastActorUid: uid ?? null,
        updatedAt: serverTimestamp(),
      }).catch(() => {});
      setOfferReply("");
      requestAnimationFrame(() => scrollToBottom("smooth"));
    } catch (e: any) {
      toast({ description: e?.message ?? "Failed to send." });
    }
  }

  /* ---------- Conversation actions (Archive / Delete) ---------- */
  async function handleArchiveThread() {
    if (!uid || !selectedTid) return;
    try {
      await updateDoc(doc(db, "threads_v2", selectedTid), {
        [`archivedFor.${uid}`]: serverTimestamp(),
      });
      toast({ description: "Conversation archived." });
    } catch {
      toast({ description: "Archive failed." });
    }
  }
  async function handleUnarchiveThread() {
    if (!uid || !selectedTid) return;
    try {
      await updateDoc(doc(db, "threads_v2", selectedTid), {
        [`archivedFor.${uid}`]: deleteField(),
      });
      toast({ description: "Conversation unarchived." });
    } catch {
      toast({ description: "Unarchive failed." });
    }
  }
  async function handleDeleteThread() {
    if (!uid || !selectedTid) return;
    const ok = window.confirm(
      "Delete this conversation for you? (It can be restored only by support.)",
    );
    if (!ok) return;
    try {
      await updateDoc(doc(db, "threads_v2", selectedTid), {
        [`deletedFor.${uid}`]: serverTimestamp(),
        [`unreadCounts.${uid}`]: 0,
      });
      setSelectedPeer(null);
      toast({ description: "Conversation deleted." });
    } catch {
      toast({ description: "Delete failed." });
    }
  }

  /* ---------- Offer actions ---------- */
  const fmtAmount = (n?: number | null) => {
    if (typeof n !== "number" || !Number.isFinite(n)) return null;
    try {
      return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
    } catch {
      return `$${n.toFixed(2)}`;
    }
  };
  async function onAccept(id: string) {
    try {
      await acceptOfferV2(id);
      toast({ description: "Offer accepted." });
    } catch (e: any) {
      toast({ description: e?.message ?? "Accept failed." });
    }
  }
  async function onReject(id: string) {
    try {
      await updateDoc(doc(db, "offers_v2", id), {
        status: "declined",
        updatedAt: serverTimestamp(),
        lastActorUid: uid ?? null,
      });
      toast({ description: "Offer declined." });
    } catch (e: any) {
      toast({ description: e?.message ?? "Decline failed." });
    }
  }
  async function onRescind(id: string) {
    try {
      await updateDoc(doc(db, "offers_v2", id), {
        status: "withdrawn",
        updatedAt: serverTimestamp(),
        lastActorUid: uid ?? null,
      });
      toast({ description: "Offer withdrawn." });
    } catch (e: any) {
      toast({ description: e?.message ?? "Withdraw failed." });
    }
  }
  async function onUpdateAmount(o: OfferDoc) {
    const raw = window.prompt(
      "New dollar amount",
      String(o.offeredPrice ?? ""),
    );
    if (raw == null) return;
    const amt = parseFloat(raw);
    if (!Number.isFinite(amt)) {
      toast({ description: "Enter a valid number." });
      return;
    }
    try {
      await updateDoc(doc(db, "offers_v2", o.id), {
        offeredPrice: amt,
        status: "countered",
        updatedAt: serverTimestamp(),
        lastActorUid: uid ?? null,
      });
      toast({ description: "Offer updated." });
    } catch (e: any) {
      toast({ description: e?.message ?? "Update failed." });
    }
  }

  /* ---------- Layout: measure topbar ---------- */
  useLayoutEffect(() => {
    const docStyle = document.documentElement.style;
    const measure = () => {
      const topbar =
        document.getElementById("app-topbar") ||
        (document.querySelector("[data-app-topbar]") as HTMLElement | null) ||
        (document.querySelector("header") as HTMLElement | null);
      const bottomNav =
        document.getElementById("app-bottom-nav") ||
        (document.querySelector("[data-app-bottom-nav]") as HTMLElement | null) ||
        null;
      const topbarH = Math.ceil(topbar?.getBoundingClientRect().height ?? 56);
      const bottomH = Math.ceil(bottomNav?.getBoundingClientRect().height ?? 64);
      docStyle.setProperty("--app-topbar-height", `${topbarH}px`);
      docStyle.setProperty("--app-bottom-nav-height", `${bottomH}px`);
    };
    measure();
    const roTop =
      "ResizeObserver" in window
        ? new ResizeObserver(() => measure())
        : null;
    const roBottom =
      "ResizeObserver" in window
        ? new ResizeObserver(() => measure())
        : null;
    try {
      const topEl =
        document.getElementById("app-topbar") ||
        document.querySelector("[data-app-topbar]") ||
        document.querySelector("header");
      topEl && roTop?.observe(topEl as Element);
    } catch {}
    try {
      const bottomEl =
        document.getElementById("app-bottom-nav") ||
        document.querySelector("[data-app-bottom-nav]");
      bottomEl && roBottom?.observe(bottomEl as Element);
    } catch {}
    window.addEventListener("resize", measure);
    return () => {
      try { roTop?.disconnect(); } catch {}
      try { roBottom?.disconnect(); } catch {}
      window.removeEventListener("resize", measure);
    };
  }, []);

  /* ---------- Render helpers ---------- */
  const inOfferMode = Boolean(selectedOfferId);
  const peerMini = userMini(selectedPeer || "");
  const activeThreadArchived = selectedRow?.archived;

  const offersForTab =
    offerTab === "received"
      ? receivedOffers
      : offerTab === "sent"
      ? sentOffers
      : completedOffers;

  // NEW: merge offers for the same card (single visual instance)
  const mergedOffersForTab = useMemo(() => {
    // Group key priority: cardRefPath > cardId > scryfall_id > cardName (lowercased)
    const groups = new Map<
      string,
      { offers: OfferDoc[]; representative: OfferDoc }
    >();
    for (const o of offersForTab) {
      const key =
        o.cardRefPath ||
        o.cardId ||
        o.scryfall_id ||
        (o.cardName ? o.cardName.toLowerCase() : o.id);
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, { offers: [o], representative: o });
      } else {
        existing.offers.push(o);
        // pick the most recently updated/created as representative
        const curTs =
          tsOrNull(existing.representative.updatedAt) ??
          tsOrNull(existing.representative.createdAt) ??
          0;
        const newTs =
          tsOrNull(o.updatedAt) ??
          tsOrNull(o.createdAt) ??
          0;
        if (newTs > curTs) existing.representative = o;
      }
    }
    return Array.from(groups.values()).sort((a, b) => {
      const at =
        tsOrNull(a.representative.updatedAt) ??
        tsOrNull(a.representative.createdAt) ??
        0;
      const bt =
        tsOrNull(b.representative.updatedAt) ??
        tsOrNull(b.representative.createdAt) ??
        0;
      return bt - at;
    });
  }, [offersForTab]);

  /* ---------- UI ---------- */

  return (
    <main
      className="w-full fixed left-0 right-0 bg-black text-white overflow-hidden flex"
      style={{
        top: "calc(var(--app-topbar-height,56px) + env(safe-area-inset-top))",
        bottom:
          "calc(var(--app-bottom-nav-height,64px) + env(safe-area-inset-bottom))",
      }}
    >
      <div className="h-full w-full flex">
        {/* Left (docked) */}
        <aside
          className="relative border-r border-gray-800 flex flex-col"
          style={{
            width: isMobile ? MOBILE_COLLAPSED_WIDTH : leftWidth,
            transition: resizing ? "none" : "width 120ms",
          }}
        >
          <div className="px-3 py-2 border-b border-gray-800 sticky top-0 bg-black/70 backdrop-blur z-10">
            {!isMobile && (
              <>
                <div className="text-sm font-semibold">Inbox</div>
                <div className="text-[11px] text-white/50">
                  Conversations
                </div>
              </>
            )}
            {isMobile && (
              <div className="text-[11px] text-white/60 font-semibold text-center">
                Chats
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="divide-y divide-gray-900/60">
              {conversations.map((row) => {
                const { username, avatar } = userMini(row.peerUid);
                const active = selectedPeer === row.peerUid;
                return (
                  <button
                    key={row.peerUid}
                    onClick={() => {
                      setSelectedPeer(row.peerUid);
                      setSelectedOfferId(null);
                      setOfferMsgs([]);
                      setOffersCollapsed(true);
                      focusComposer();
                    }}
                    className={[
                      "w-full text-left flex items-center gap-3 hover:bg-white/5 px-2",
                      active ? "bg-white/10" : "",
                      "py-2",
                    ].join(" ")}
                    title={`@${username}`}
                  >
                    <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-700 bg-white/5 shrink-0">
                      {row.peerUid === SCOREBOARD_BOT_UID ? (
                        <img
                          src={BOT_AVATAR_SRC}
                          alt="scoreboard bot"
                          className="w-full h-full object-cover"
                        />
                      ) : avatar ? (
                        <img
                          src={avatar}
                          alt={username}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            try {
                              (e.currentTarget as HTMLImageElement).src =
                                "/images/avatar-fallback.png";
                            } catch {}
                          }}
                        />
                      ) : (
                        <div className="grid place-items-center text-[10px] text-white/70 font-bold">
                          {username.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                    </div>
                    {!isMobile && (
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-semibold truncate">
                            @{username}
                          </div>
                          {!!row.unread && (
                            <span className="text-[10px] px-1 py-px rounded bg-amber-500 text-black">
                              {row.unread}
                            </span>
                          )}
                          {row.archived && (
                            <span className="text-[9px] px-1 py-px rounded bg-white/10">
                              A
                            </span>
                          )}
                          <div className="ml-auto text-[10px] text-white/50">
                            {row.lastMessageAt
                              ? new Date(
                                  tsMillis(row.lastMessageAt),
                                ).toLocaleDateString()
                              : ""}
                          </div>
                        </div>
                        <div className="text-[11px] text-white/60 truncate">
                          {row.lastMessage || "Say hello 👋"}
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
              {!conversations.length && (
                <div className="px-3 py-6 text-white/60 text-sm">
                  No conversations yet.
                </div>
              )}
            </div>
          </div>
          {!isMobile && (
            <div
              onMouseDown={onDragStart}
              className="absolute top-0 right-0 h-full w-1 cursor-col-resize group"
            >
              <div className="w-[3px] h-full mx-auto opacity-0 group-hover:opacity-100 bg-amber-500/50 transition rounded"></div>
            </div>
          )}
        </aside>

        {/* Right side (flex column, header + offers + messages + composer) */}
        <section className="flex-1 min-w-0 flex flex-col relative">
          {/* Header */}
            <div className="px-4 py-3 border-b border-gray-800 bg-black/70 backdrop-blur flex items-center gap-3">
            {selectedPeer ? (
              <>
                <button
                  className="flex items-center gap-3"
                  onClick={() => {
                    if (inOfferMode) {
                      setSelectedOfferId(null);
                      focusComposer();
                    } else {
                      navigate(`/${peerMini.username}`);
                    }
                  }}
                  title={
                    inOfferMode
                      ? "Back to DMs"
                      : `View @${peerMini.username} profile`
                  }
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-700 bg-white/5">
                    {selectedPeer === SCOREBOARD_BOT_UID ? (
                      <img
                        src={BOT_AVATAR_SRC}
                        alt="scoreboard bot"
                        className="w-full h-full object-cover"
                      />
                    ) : peerMini.avatar ? (
                      <img
                        src={peerMini.avatar}
                        alt={peerMini.username}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          try {
                            (e.currentTarget as HTMLImageElement).src =
                              "/images/avatar-fallback.png";
                          } catch {}
                        }}
                      />
                    ) : (
                      <div className="grid place-items-center text-[10px] text-white/70 font-bold">
                        {peerMini.username.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="text-sm font-semibold">
                    @{peerMini.username}
                    {inOfferMode && (
                      <span className="ml-2 text-[11px] text-white/60">
                        (offer)
                      </span>
                    )}
                    {activeThreadArchived && (
                      <span className="ml-2 text-[10px] text-white/50">
                        • Archived
                      </span>
                    )}
                  </div>
                </button>
                <div className="ml-auto flex items-center gap-2">
                  {activeThreadArchived ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => void handleUnarchiveThread()}
                    >
                      Unarchive
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => void handleArchiveThread()}
                    >
                      Archive
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs border-red-700 text-red-300 hover:bg-red-900/30"
                    onClick={() => void handleDeleteThread()}
                  >
                    Delete
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-white/70 text-sm">
                Select a conversation
              </div>
            )}
          </div>

          {/* Offers bar (collapsible) */}
          {!!selectedPeer && (
            <div className="border-b border-gray-800 bg-black/50 backdrop-blur">
              <div className="flex items-center px-3 py-1.5 gap-2">
                <button
                  onClick={() => setOffersCollapsed((c) => !c)}
                  className="text-[11px] px-2 py-1 rounded border border-gray-700 hover:bg-white/10"
                >
                  {offersCollapsed
                    ? "Show offers"
                    : "Hide offers"}{" "}
                  ({receivedOffers.length + sentOffers.length + completedOffers.length})
                </button>
                {!offersCollapsed && (
                  <div className="inline-flex rounded-md overflow-hidden border border-gray-700 ml-2">
                    {(["received", "sent", "completed"] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => setOfferTab(t)}
                        className={`px-2.5 py-1 text-[11px] ${
                          offerTab === t
                            ? "bg-white/15 text-white"
                            : "text-white/70 hover:text-white"
                        }`}
                      >
                        {t === "received"
                          ? "Received"
                          : t === "sent"
                          ? "Sent"
                          : "Done"}
                      </button>
                    ))}
                  </div>
                )}
                <div className="ml-auto text-[11px] text-white/60">
                  Offers with @{peerMini.username}
                </div>
              </div>
              {!offersCollapsed && (
                <div className="px-3 pb-2">
                  <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
                    {mergedOffersForTab.length === 0 && (
                      <div className="text-[12px] text-white/50 px-1 py-1">
                        No {offerTab} offers.
                      </div>
                    )}
                    {mergedOffersForTab.map((g) => {
                      const rep = g.representative;
                      // pick first image available among grouped offers
                      const img =
                        offerThumbs[rep.id]?.image ||
                        rep.cardImage ||
                        g.offers
                          .map(
                            (o) => offerThumbs[o.id]?.image || o.cardImage || null,
                          )
                          .find(Boolean) ||
                        null;
                      // highlight if currently selected offer is in this group
                      const isSelected = g.offers.some(
                        (o) => o.id === selectedOfferId,
                      );
                      return (
                        <button
                          key={rep.id}
                          title={
                            rep.cardName ||
                            offerThumbs[rep.id]?.name ||
                            "Card"
                          }
                          onClick={() => {
                            // choose latest (representative) for selection
                            setSelectedOfferId(rep.id);
                            setOfferMsgs([]);
                            setOffersCollapsed(true);
                            focusOfferComposer();
                          }}
                          className={[
                            "relative shrink-0 overflow-hidden rounded-md",
                            isSelected
                              ? "ring-2 ring-amber-400"
                              : "ring-1 ring-gray-700 hover:ring-gray-500",
                            "bg-black/40",
                          ].join(" ")}
                          style={{
                            width: 160,
                            aspectRatio: "488/680", // approximate MTG card ratio
                          }}
                        >
                          {img ? (
                            <img
                              src={img}
                              alt=""
                              className="w-full h-full object-cover select-none pointer-events-none"
                              onError={(e) => {
                                try {
                                  (e.currentTarget as HTMLImageElement).style.opacity =
                                    "0.4";
                                } catch {}
                              }}
                            />
                          ) : (
                            <div className="w-full h-full grid place-items-center text-[10px] text-white/40">
                              …
                            </div>
                          )}
                          {/* Small badge with count if multiple offers merged */}
                          {g.offers.length > 1 && (
                            <div className="absolute top-1 left-1 text-[10px] px-1 rounded bg-black/60 border border-white/10 text-white/70">
                              {g.offers.length}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Inline selected offer summary (optional) */}
              {inOfferMode && selectedOfferId && (
                <div className="px-3 pb-2">
                  <OfferInline
                    uid={uid}
                    meIsAdmin={amAdmin}
                    offer={offers.find((o) => o.id === selectedOfferId)!}
                    thumb={offerThumbs[selectedOfferId]}
                    userMini={userMini}
                    fmtAmount={fmtAmount}
                    onAccept={onAccept}
                    onReject={onReject}
                    onRescind={onRescind}
                    onUpdateAmount={onUpdateAmount}
                  />
                </div>
              )}
            </div>
          )}

          {/* Scroll area (ONLY messages / offer messages) */}
          <div
            ref={listRef}
            className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3"
            style={{
              scrollPaddingBottom: "160px",
            }}
          >
            {!selectedPeer && (
              <div className="text-white/70">
                Pick a conversation on the left.
              </div>
            )}
            {!!selectedPeer && !inOfferMode && (
              <>
                {messages.map((m) => {
                  const mine = m.senderId === uid;
                  const text = msgText(m);
                  const atts = ((m.body as any)?.attachments ||
                    []) as MsgAttachment[];
                  const pStatus = (m as any)?._pendingStatus as
                    | "sending"
                    | "failed"
                    | undefined;
                  return (
                    <div
                      key={m.id}
                      className={`max-w-[75%] ${mine ? "ml-auto" : ""}`}
                    >
                      <div
                        className={`px-3 py-2 rounded-xl ${
                          mine
                            ? "bg-blue-500/20 text-amber-100"
                            : "bg-white/10 text-white"
                        }`}
                      >
                        {text && (
                          <div className="whitespace-pre-wrap break-words">
                            {text}
                          </div>
                        )}
                        {!!atts.length && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {atts.map((a, i) =>
                              a.type === "image" ? (
                                <a
                                  key={`${m.id}-att-${i}`}
                                  href={a.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block w-24 h-24 rounded-md overflow-hidden border border-white/20 bg-black/30"
                                  title={a.name}
                                >
                                  <img
                                    src={a.url}
                                    alt={a.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      (e.currentTarget as HTMLImageElement).src = "/images/attachment-expired.png";
                                    }}
                                  />
                                </a>
                              ) : (
                                <a
                                  key={`${m.id}-att-${i}`}
                                  href={a.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded border border-white/20 hover:bg-white/10"
                                  title={`${a.name} (${Math.ceil(
                                    a.size / 1024,
                                  )} KB)`}
                                >
                                  <span className="truncate max-w-[1100px]">
                                    {a.name}
                                  </span>
                                </a>
                              ),
                            )}
                          </div>
                        )}
                        <div className="text-[11px] opacity-70 mt-1">
                          {new Date(tsMillis(m.createdAt)).toLocaleString()}
                          {mine
                            ? pStatus === "sending"
                              ? " • Sending…"
                              : pStatus === "failed"
                              ? " • Failed"
                              : m.isRead
                              ? " • Read"
                              : " • Sent"
                            : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!messages.length && (
                  <div className="text-white/70">No messages yet.</div>
                )}
              </>
            )}
            {!!selectedPeer && inOfferMode && (
              <>
                {offerMsgs.length === 0 && (
                  <div className="text-white/70 text-sm">
                    No offer messages yet.
                  </div>
                )}
                {offerMsgs.map((m) => {
                  const mine = m.senderUid === uid;
                  const t =
                    m.createdAt && (m.createdAt as any).toMillis
                      ? new Date((m.createdAt as any).toMillis()).toLocaleString()
                      : "";
                  return (
                    <div
                      key={m.id}
                      className={`max-w-[80%] ${mine ? "ml-auto" : ""}`}
                    >
                      <div
                        className={`px-3 py-2 rounded-xl text-xs whitespace-pre-wrap ${
                          mine
                            ? "bg-blue-500/25 text-amber-100"
                            : m._synthetic
                            ? "bg-white/5 text-white/80 italic"
                            : "bg-white/10 text-white"
                        }`}
                      >
                        {m.text || "(empty)"}
                        <div className="mt-1 text-[10px] opacity-60">
                          {t}
                          {m._synthetic ? " • original" : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Composer (docked) */}
          {!!selectedPeer && (
            <div className="border-t border-gray-800 bg-black/75 backdrop-blur px-3 py-3">
              {!inOfferMode ? (
                <Composer
                  input={input}
                  setInput={setInput}
                  files={files}
                  setFiles={setFiles}
                  uploading={uploading}
                  onSend={() => void handleSendDM()}
                  fileInputRef={fileInputRef}
                  textareaRef={textareaRef}
                  ACCEPT={ATTACH_ACCEPT as any}
                  MAX_BYTES={ATTACH_MAX_BYTES}
                  MAX_COUNT={ATTACH_MAX_COUNT}
                />
              ) : (
                <OfferComposer
                  value={offerReply}
                  setValue={setOfferReply}
                  onSend={() => void handleSendOfferMsg()}
                  textareaRef={offerTextareaRef}
                />
              )}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

/* ================= Inline Components ================= */

function Composer({
  input,
  setInput,
  files,
  setFiles,
  uploading,
  onSend,
  fileInputRef,
  textareaRef,
  ACCEPT,
  MAX_BYTES,
  MAX_COUNT,
}: {
  input: string;
  setInput: (s: string) => void;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  uploading: boolean;
  onSend: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  ACCEPT: readonly string[];
  MAX_BYTES: number;
  MAX_COUNT: number;
}) {
  const [processing, setProcessing] = useState(false);

  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() || files.length) onSend();
    }
  }

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    if (processing) return;
    setProcessing(true);
    const next: File[] = [...files];
    for (const raw of picked) {
      if (next.length >= MAX_COUNT) break;
      if (!ACCEPT.includes(raw.type as any)) {
        toast({ description: `Unsupported file type: ${raw.type}` });
        continue;
      }
      // Ensure / process per spec
      const result = await ensureImageCompliance(raw);
      if (!result.ok || !result.file) {
        toast({
          description: result.reason
            ? `Skipped: ${result.reason}`
            : "Skipped file",
        });
        continue;
      }
      if (result.warnings?.length) {
        toast({
          description: result.warnings.join(" • "),
        });
      }
      if (result.file.size > MAX_BYTES) {
        toast({
          description: `Final file exceeds limit (${(
            result.file.size /
            1024 /
            1024
          ).toFixed(2)}MB)`,
        });
        continue;
      }
      next.push(result.file);
    }
    setFiles(next);
    setProcessing(false);
  }

  function removeFile(i: number) {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  }

  return (
    <div className="space-y-2">
      {!!files.length && (
        <div className="flex flex-wrap gap-2">
          {files.map((f, i) => {
            const isImg = f.type.startsWith("image/");
            return (
              <div
                key={i}
                className="relative group w-16 h-16 rounded-md overflow-hidden border border-gray-700 bg-black/40"
              >
                {isImg ? (
                  <img
                    src={URL.createObjectURL(f)}
                    alt={f.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-[10px] text-white/70 px-1 text-center break-words">
                    {f.name.slice(0, 18)}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="absolute top-0 right-0 px-1 py-0.5 text-[10px] bg-black/70 text-white rounded-bl opacity-0 group-hover:opacity-100 transition"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            );
          })}
          {files.length < MAX_COUNT && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-16 h-16 rounded-md border border-dashed border-gray-600 text-white/50 text-[11px] hover:border-white/60 hover:text-white flex items-center justify-center"
            >
              +
            </button>
          )}
        </div>
      )}

      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          className="flex-1 resize-none rounded border border-gray-700 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-amber-400/40 max-h-40 disabled:opacity-50"
          placeholder="Message…"
          value={input}
          disabled={processing}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          rows={Math.min(6, Math.max(2, input.split("\n").length))}
        />
        <div className="flex flex-col gap-2 w-[120px] shrink-0">
          <Button
            type="button"
            variant="outline"
            className="w-full border-gray-700 text-white hover:bg-white/10 text-xs disabled:opacity-50"
            disabled={uploading || processing}
            onClick={() => fileInputRef.current?.click()}
          >
            {processing
              ? "Processing…"
              : uploading
              ? "Uploading…"
              : files.length
              ? "Add Img"
              : "Attach"}
          </Button>
          <Button
            type="button"
            className="w-full bg-amber-500 hover:bg-amber-400 text-black text-sm disabled:opacity-50"
            disabled={
              uploading || processing || (!input.trim() && !files.length)
            }
            onClick={onSend}
          >
            Send
          </Button>
        </div>
      </div>

      <div className="flex justify-between text-[10px] text-white/40 px-1">
        <span>
          {files.length}/{MAX_COUNT} images • 8MB max • ratios 1:1 / 4:5 / 1.91:1
        </span>
        <span>Enter = send</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        accept={RAW_ACCEPT_MIME.join(",")}
        onChange={onPick}
      />
    </div>
  );
}
function OfferComposer({
  value,
  setValue,
  onSend,
  textareaRef,
}: {
  value: string;
  setValue: (s: string) => void;
  onSend: () => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
}) {
  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) onSend();
    }
  }
  return (
    <div className="flex items-end gap-2">
      <textarea
        ref={textareaRef}
        className="flex-1 resize-none rounded border border-gray-700 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-amber-400/40 max-h-40"
        placeholder="Reply about this offer…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKey}
        rows={Math.min(6, Math.max(2, value.split('\n').length))}
      />
      <Button
        type="button"
        className="w-[110px] bg-amber-500 hover:bg-amber-400 text-black text-sm disabled:opacity-50"
        disabled={!value.trim()}
        onClick={onSend}
      >
        Send
      </Button>
    </div>
  );
}
function OfferInline({
  uid,
  meIsAdmin,
  offer,
  thumb,
  userMini,
  fmtAmount,
  onAccept,
  onReject,
  onRescind,
  onUpdateAmount,
}: {
  uid: string | null;
  meIsAdmin: boolean;
  offer: any;
  thumb?: { name?: string | null; image?: string | null };
  userMini: (uid?: string | null) => { username: string; avatar: string | null };
  fmtAmount: (n?: number | null) => string | null;
  onAccept: (id: string) => void | Promise<void>;
  onReject: (id: string) => void | Promise<void>;
  onRescind: (id: string) => void | Promise<void>;
  onUpdateAmount: (o: any) => void | Promise<void>;
}) {
  if (!offer) return null;
  const isSender = !!uid && offer.fromUid === uid;
  const isRecipient = !!uid && offer.toUid === uid;
  const open = offer.status === "open" || offer.status === "countered";
  const cardName =
    (offer.cardName && offer.cardName !== "__none__"
      ? offer.cardName
      : thumb?.name) || "Card";
  const imgSrc = thumb?.image || offer.cardImage || null;
  return (
    <div className="rounded-lg border border-gray-800 bg-black/30 p-3">
      <div className="flex items-start gap-3">
        <div className="w-20 h-28 rounded-md overflow-hidden border border-gray-700 bg-white/5 shrink-0">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={cardName}
              className="w-full h-full object-cover"
              onError={(e) => {
                try {
                  (e.currentTarget as HTMLImageElement).style.opacity = "0.4";
                } catch {}
              }}
            />
          ) : (
            <div className="w-full h-full grid place-items-center text-[10px] text-white/50">
              …
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
                   <div className="text-sm font-semibold">{cardName}</div>
          <div className="text-[12px] text-white/70">
            Type: {offer.offerType}
            {typeof offer.offeredPrice === "number" &&
              ` • ${fmtAmount(offer.offeredPrice)}`}
          </div>
          <div className="text-[11px] text-white/60 mt-1">
            Created: {offer.createdAt ? new Date(offer.createdAt.toMillis?.() || 0).toLocaleString() : "…"} • Updated:{" "}
            {offer.updatedAt
              ? new Date(offer.updatedAt.toMillis?.() || 0).toLocaleString()
              : offer.createdAt
              ? new Date(offer.createdAt.toMillis?.() || 0).toLocaleString()
              : "…"}
          </div>
          {!!offer.message && (
            <div className="text-[12px] text-white/80 mt-2">“{offer.message}”</div>
          )}
        </div>
        <div className="text-[10px] px-2 py-1 rounded bg-white/10">
          {offer.status.toUpperCase()}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {open && isRecipient && (
          <>
            <Button
              className="bg-emerald-500 hover:bg-emerald-400 text-black text-xs"
              onClick={() => onAccept(offer.id)}
            >
              Accept
            </Button>
            <Button
              variant="outline"
              className="border-red-700 text-red-300 hover:bg-red-900/30 text-xs"
              onClick={() => onReject(offer.id)}
            >
              Decline
            </Button>
            <Button
              variant="outline"
              className="border-amber-700 text-amber-300 hover:bg-amber-900/30 text-xs"
              onClick={() => onUpdateAmount(offer)}
            >
              Counter
            </Button>
          </>
        )}
        {open && isSender && (
          <>
            <Button
              variant="outline"
              className="border-amber-700 text-amber-300 hover:bg-amber-900/30 text-xs"
              onClick={() => onUpdateAmount(offer)}
            >
              Update
            </Button>
            <Button
              variant="outline"
              className="border-red-700 text-red-300 hover:bg-red-900/30 text-xs"
              onClick={() => onRescind(offer.id)}
            >
              Withdraw
            </Button>
          </>
        )}
        {!open && (
          <div className="text-[11px] text-white/60">
            This offer is {offer.status}.
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= Debugging Helpers (NEW) ================= */

async function debugThread(tid: string) {
  try {
    const snap = await getDoc(doc(db, "threads_v2", tid));
    if (!snap.exists()) {
      console.warn("[attach][debug] thread missing", tid);
      return;
    }
    const d: any = snap.data();
    console.log("[attach][debug] thread shape", {
      tid,
      participants: d.participants,
      fromUid: d.fromUid,
      toUid: d.toUid,
      visibleFor: d.visibleFor,
    });
  } catch (e) {
    console.warn("[attach][debug] thread fetch error", e);
  }
}

// Backfill participants / visibleFor for legacy threads
async function ensureThreadParticipants(tid: string, me: string, peer: string) {
  try {
    const ref = doc(db, "threads_v2", tid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      // If truly missing let resolveThreadId handle creation later
      return;
    }
    const d: any = snap.data();
    if (Array.isArray(d.participants) && d.participants.length === 2) {
      return;
    }
    // Accept legacy shapes fromUid/toUid
    const inferred: string[] = [];
    if (typeof d.fromUid === "string") inferred.push(d.fromUid);
    if (typeof d.toUid === "string" && d.toUid !== d.fromUid) inferred.push(d.toUid);
    if (!inferred.includes(me)) inferred.push(me);
    if (!inferred.includes(peer)) inferred.push(peer);
    if (inferred.length === 2) {
      await setDoc(
        ref,
        {
          participants: inferred.sort(),
          visibleFor: inferred.sort(),
        },
        { merge: true },
      );
      console.log("[attach][debug] participants backfilled", inferred);
    } else {
      console.warn("[attach][debug] could not infer participants", { tid, inferred });
    }
  } catch (e) {
    console.warn("[attach][debug] ensureThreadParticipants failed", e);
  }
}

